import type {
  ICodeBranchesResponse,
  ICodeRepositoriesResponse,
  ICodeSessionDetail,
  ICodeSessionEventsPage,
  ICodeSessionListItem,
  ICreateCodeSessionParams,
  IGetCodeBranchesParams,
  IGetCodeRepositoriesParams,
  IGetCodeSessionEventsParams,
  IGetCodeSessionListParams,
} from "../types";
import type { ApiClient } from "./client";

const buildAccountQuery = (accountTitle?: string): string =>
  accountTitle ? `?account=${encodeURIComponent(accountTitle)}` : "";

export class CodeSessionApi {
  constructor(private readonly apiClient: ApiClient) {}

  async getCodeSessionList(
    params: IGetCodeSessionListParams,
  ): Promise<ICodeSessionListItem[]> {
    const { limit, offset } = params;
    const response = await this.apiClient.get<ICodeSessionListItem[]>(
      `/code-sessions?limit=${limit}&offset=${offset}`,
    );
    return response.data;
  }

  async getCodeRepositories({
    accountTitle,
  }: IGetCodeRepositoriesParams = {}): Promise<ICodeRepositoriesResponse> {
    const response = await this.apiClient.get<ICodeRepositoriesResponse>(
      `/code-sessions/repositories${buildAccountQuery(accountTitle)}`,
    );
    return response.data;
  }

  async getCodeBranches({
    owner,
    repo,
    accountTitle,
  }: IGetCodeBranchesParams): Promise<ICodeBranchesResponse> {
    const response = await this.apiClient.get<ICodeBranchesResponse>(
      `/code-sessions/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches${buildAccountQuery(accountTitle)}`,
    );
    return response.data;
  }

  async getCodeSession(uuid: string): Promise<ICodeSessionDetail> {
    const response = await this.apiClient.get<ICodeSessionDetail>(
      `/code-sessions/${uuid}`,
    );
    return response.data;
  }

  async getCodeSessionEvents(
    params: IGetCodeSessionEventsParams,
  ): Promise<ICodeSessionEventsPage> {
    const { uuid, afterSequence, limit } = params;
    const queryParts: string[] = [];
    if (afterSequence !== undefined) {
      queryParts.push(`afterSequence=${afterSequence}`);
    }
    if (limit !== undefined) {
      queryParts.push(`limit=${limit}`);
    }
    const suffix = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";
    const response = await this.apiClient.get<ICodeSessionEventsPage>(
      `/code-sessions/${uuid}/events${suffix}`,
    );
    return response.data;
  }

  async createCodeSession({
    payload,
    idempotencyKey,
  }: ICreateCodeSessionParams): Promise<ICodeSessionDetail> {
    const response = await this.apiClient.post<ICodeSessionDetail>(
      "/code-sessions",
      payload,
      idempotencyKey
        ? { headers: { "Idempotency-Key": idempotencyKey } }
        : undefined,
    );
    return response.data;
  }

  async cancelCodeSession(uuid: string): Promise<void> {
    await this.apiClient.post(`/code-sessions/${uuid}/cancel`);
  }
}
